import {
  DEMO_EPSS,
  DEMO_KODEM_SCORE,
  DEMO_PROJECT_NAME,
  DEMO_RUNTIME_EVIDENCE_TEXT,
  TARGET_RISK_ID
} from "./MitigationDemoCards.const"

import type {
  IssuesTriageResponse,
  ProjectsTriageResponse
} from "@/api/useTriage.types"
import type { InsightsIssue, Issue, Project } from "@/domain/issue"

// Demo-only enrichment for the kodemsecurity "Exploit Validations & Mitigations"
// concept. Applied to the RAW API response BEFORE the triage selectors run, so
// every derived field (score, EPSS, the insight flags computed from
// riskInsights) reflects the enriched values consistently in the list, the
// drawer, and the owning project. Everything demo-specific lives here so the
// shared selectors stay tenant-agnostic and removing the demo is a clean delete.

const enrichRiskInsights = (insights: InsightsIssue): InsightsIssue => ({
  ...insights,
  runtime: true,
  ingress: false,
  internetFacing: "YES",
  exploitMaturity: "POC"
})

const enrichRawIssue = (issue: Issue): Issue =>
  issue.riskId === TARGET_RISK_ID
    ? {
        ...issue,
        kodemScore: DEMO_KODEM_SCORE,
        epss: DEMO_EPSS,
        riskInsights: enrichRiskInsights(issue.riskInsights),
        runtimeData: {
          ...issue.runtimeData,
          keyEvidence: {
            ...issue.runtimeData?.keyEvidence,
            functions: issue.runtimeData?.keyEvidence?.functions ?? [],
            classes: issue.runtimeData?.keyEvidence?.classes ?? [],
            files: issue.runtimeData?.keyEvidence?.files ?? [],
            processes: issue.runtimeData?.keyEvidence?.processes ?? [],
            isExecuted: issue.runtimeData?.keyEvidence?.isExecuted ?? false,
            type: "package_in_runtime",
            displayText: DEMO_RUNTIME_EVIDENCE_TEXT,
            isRuntime: true
          }
        }
      }
    : issue

const isDemoIssue = (issue: Issue) => issue.riskId === TARGET_RISK_ID
const isDemoProject = (project: Project) => project.name === DEMO_PROJECT_NAME

const enrichRawProject = (project: Project): Project =>
  isDemoProject(project)
    ? {
        ...project,
        kodemScore: DEMO_KODEM_SCORE,
        riskInsights: {
          ...project.riskInsights,
          runtime: true,
          ingress: false,
          internetFacing: "YES",
          exploitMaturities: ["POC"]
        }
      }
    : project

// Active triage sort. orderBy is the column key; empty means the server default
// (Kodem score, descending). ascending mirrors the "ascending" query param.
export interface TriageSort {
  orderBy?: string
  ascending?: boolean
}

const SCORE_ORDER_KEY = "kodemScore"

// The server orders/paginates by the REAL score, so the enriched demo row keeps
// its original rank. When the list is ordered by Kodem score (the default),
// re-sort the returned rows by the enriched score so the demo row lands in its
// correct position — not pinned to the top. Array sort is stable, so every other
// row keeps the server's ordering. For any other sort column the enriched score
// does not affect position, so the order is left untouched.
export const reorderByScore = <T>(
  rows: T[],
  sort: TriageSort,
  score: (row: T) => number
): T[] => {
  if (sort.orderBy && sort.orderBy !== SCORE_ORDER_KEY) return rows
  const direction = sort.ascending ? 1 : -1
  return [...rows].sort((a, b) => (score(a) - score(b)) * direction)
}

export const enrichDemoIssuesResponse = <R extends { data?: Issue | Issue[] }>(
  response: R,
  enabled: boolean
): R => {
  const data = response?.data
  if (!enabled || !data) return response
  return {
    ...response,
    data: Array.isArray(data) ? data.map(enrichRawIssue) : enrichRawIssue(data)
  } as R
}

export const enrichDemoIssuesPages = (
  response: { pages: IssuesTriageResponse[] },
  enabled: boolean,
  sort: TriageSort
) => {
  if (!enabled) return response

  const pages = response.pages.map((page) => ({
    ...page,
    data: (page.data ?? []).map(enrichRawIssue)
  }))

  // Float the demo issue onto the first page (even if the server returned it on
  // a later page) so it is reachable, then let the score re-sort place it.
  const sourceIndex = pages.findIndex((page) => page.data.some(isDemoIssue))
  if (sourceIndex > 0 && pages[0]) {
    const source = pages[sourceIndex]
    const issue = source.data.find(isDemoIssue)!
    pages[sourceIndex] = {
      ...source,
      data: source.data.filter((item) => !isDemoIssue(item))
    }
    pages[0] = { ...pages[0], data: [issue, ...pages[0].data] }
  }
  if (pages[0]) {
    pages[0] = {
      ...pages[0],
      data: reorderByScore(pages[0].data, sort, (issue) => issue.kodemScore)
    }
  }

  return { ...response, pages }
}

export const enrichDemoProjectsResponse = (
  response: ProjectsTriageResponse,
  enabled: boolean,
  sort: TriageSort
): ProjectsTriageResponse => {
  const data = response?.data
  if (!enabled || !data) return response
  return {
    ...response,
    data: reorderByScore(
      data.map(enrichRawProject),
      sort,
      (project) => project.kodemScore
    )
  }
}
